// Shared formatting helpers + berechnete Tages-Status-Map fuer die
// Arbeitszeituebersicht.
//
// Tagestyp-Konstanten — werden auch im EmployeeTimeDrawer fuer Farb-Codes genutzt.
export const DAY_TYPES = {
  WORKED: 'worked',
  PARTIAL: 'partial',          // Mitarbeiter eingestempelt aber Soll < voll
  MISSING: 'missing',          // Werktag ohne Buchung, keine Abwesenheit
  VACATION: 'vacation',
  VACATION_HALF: 'vacation_half',
  SICK: 'sick',
  ABSENCE_OTHER: 'absence_other',
  HOLIDAY: 'holiday',
  WEEKEND: 'weekend',
  FUTURE: 'future',
};

export const DAY_TYPE_LABELS = {
  worked: 'Gearbeitet',
  partial: 'Teilweise gearbeitet',
  missing: 'Keine Buchung',
  vacation: 'Urlaub',
  vacation_half: 'Halbtags Urlaub',
  sick: 'Krank',
  absence_other: 'Abwesend',
  holiday: 'Feiertag',
  weekend: 'Wochenende',
  future: 'Zukunft',
};

// Tailwind-Klassen-Map fuer Tages-Pillen im Detail-Drawer
export const DAY_TYPE_STYLE = {
  worked: 'bg-emerald-100 text-emerald-700 border-emerald-300',
  partial: 'bg-amber-100 text-amber-700 border-amber-300',
  missing: 'bg-rose-100 text-rose-700 border-rose-300',
  vacation: 'bg-sky-100 text-sky-700 border-sky-300',
  vacation_half: 'bg-sky-50 text-sky-700 border-sky-200',
  sick: 'bg-orange-100 text-orange-700 border-orange-300',
  absence_other: 'bg-purple-100 text-purple-700 border-purple-300',
  holiday: 'bg-red-100 text-red-700 border-red-300',
  weekend: 'bg-stone-100 text-stone-500 border-stone-200',
  future: 'bg-stone-50 text-stone-400 border-stone-200',
};

export const formatTime = (isoString) => {
  if (!isoString) return '-';
  const date = new Date(isoString);
  return date.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
};

export const formatMinutes = (minutes) => {
  if (!minutes && minutes !== 0) return '-';
  const sign = minutes < 0 ? '-' : '';
  const abs = Math.abs(minutes);
  const hours = Math.floor(abs / 60);
  const mins = abs % 60;
  return `${sign}${hours}:${mins.toString().padStart(2, '0')}`;
};

export const formatMinutesDecimal = (minutes) => {
  if (!minutes && minutes !== 0) return '';
  return (minutes / 60).toFixed(2);
};

export const formatDate = (dateStr) => {
  const date = new Date(dateStr);
  return date.toLocaleDateString('de-DE', { weekday: 'short', day: '2-digit', month: '2-digit' });
};

export function getMonthStart() {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
}

export function getMonthEnd() {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().split('T')[0];
}

export function getWeekStart() {
  const now = new Date();
  const day = now.getDay();
  const diff = now.getDate() - day + (day === 0 ? -6 : 1);
  return new Date(now.setDate(diff)).toISOString().split('T')[0];
}

export function getWeekEnd() {
  const start = new Date(getWeekStart());
  start.setDate(start.getDate() + 6);
  return start.toISOString().split('T')[0];
}

// ----------------------------------------------------------------------------
// Helfer: alle Datumsstrings (YYYY-MM-DD) zwischen start/end inklusive,
// in chronologischer Reihenfolge.
// ----------------------------------------------------------------------------
function eachDate(startStr, endStr) {
  const out = [];
  const s = new Date(startStr);
  const e = new Date(endStr);
  for (let d = new Date(s); d <= e; d.setDate(d.getDate() + 1)) {
    out.push(d.toISOString().split('T')[0]);
  }
  return out;
}

// Erkenne halbtags-Urlaub heuristisch: vacation_type enthaelt "halb" (case-insensitive).
// Modell-Erweiterung um is_half_day folgt in Phase 2 — wenn das Feld existiert,
// nutzen wir es bevorzugt.
function isVacationHalfDay(v) {
  if (v?.is_half_day === true) return true;
  const t = (v?.vacation_type || '').toLowerCase();
  return t.includes('halb');
}

// ----------------------------------------------------------------------------
// Tages-Status-Map fuer eine:n Mitarbeiter:in.
// Reihenfolge der Prioritaeten (hoechste Prioritaet zuerst):
//   1) Wochenende
//   2) Feiertag (auch wenn am Wochenende — selten relevant, aber konsistent)
//   3) Approved Vacation (ganz/halb)
//   4) Absence (Krank etc.)
//   5) Time-Entry vorhanden (worked / partial)
//   6) Werktag ohne Buchung — `missing`, oder `future` wenn nach heute
// ----------------------------------------------------------------------------
export function buildDayStatusMap({
  employeeId,
  dateRange,
  workEntries,
  vacations,
  absences,
  holidayDateSet,
  dailyMinutesTarget,
}) {
  const dates = eachDate(dateRange.start, dateRange.end);
  const today = new Date().toISOString().split('T')[0];
  const map = {};

  // Index pro Datum aufbauen
  const workByDate = {};
  workEntries
    .filter((e) => e.employee_id === employeeId)
    .forEach((e) => { workByDate[e.date] = e; });

  // Vacation-Ranges expandieren zu einzelnen Tagen
  const vacationByDate = {};
  (vacations || [])
    .filter((v) => v.employee_id === employeeId && v.status === 'approved')
    .forEach((v) => {
      const half = isVacationHalfDay(v);
      eachDate(v.start_date, v.end_date).forEach((d) => {
        // bei Halbtag: nur eintragen, wenn noch nicht voll markiert
        if (vacationByDate[d] === 'full') return;
        vacationByDate[d] = half ? 'half' : 'full';
      });
    });

  // Absence-Ranges expandieren
  const absenceByDate = {};
  (absences || [])
    .filter((a) => a.employee_id === employeeId)
    .forEach((a) => {
      eachDate(a.start_date, a.end_date).forEach((d) => {
        // Sick (K/KO/KK) vs sonstige (EZ etc.)
        const t = a.absence_type;
        absenceByDate[d] = (t === 'K' || t === 'KO' || t === 'KK') ? 'sick' : 'absence_other';
      });
    });

  dates.forEach((d) => {
    const dow = new Date(d).getDay(); // 0=So, 6=Sa
    const isWeekend = dow === 0 || dow === 6;

    let type = DAY_TYPES.MISSING;
    let workedMinutes = 0;

    if (isWeekend) type = DAY_TYPES.WEEKEND;
    else if (holidayDateSet.has(d)) type = DAY_TYPES.HOLIDAY;
    else if (vacationByDate[d] === 'full') type = DAY_TYPES.VACATION;
    else if (vacationByDate[d] === 'half') type = DAY_TYPES.VACATION_HALF;
    else if (absenceByDate[d] === 'sick') type = DAY_TYPES.SICK;
    else if (absenceByDate[d] === 'absence_other') type = DAY_TYPES.ABSENCE_OTHER;
    else if (workByDate[d]) {
      const net = workByDate[d].net_work_minutes || 0;
      workedMinutes = net;
      if (net >= dailyMinutesTarget * 0.5) type = DAY_TYPES.WORKED;
      else type = DAY_TYPES.PARTIAL;
    } else if (d > today) {
      type = DAY_TYPES.FUTURE;
    } else {
      type = DAY_TYPES.MISSING;
    }

    map[d] = {
      date: d,
      type,
      workedMinutes,
      timeEntryId: workByDate[d]?.id || null,
      holidayName: holidayDateSet.has(d) ? null : null, // wird vom Renderer aus holidayMap erganzt
    };
  });

  return map;
}

// ----------------------------------------------------------------------------
// Aggregat-Summary pro Mitarbeiter:in mit korrekter Soll-/Ist-Bilanz.
//
// Regel: ein Urlaubs- oder Krankheitstag reduziert das Soll, KEIN Credit
// (=bilanz-neutral). Feiertage werden ebenfalls vom Soll abgezogen.
// Halbtags-Urlaub: nur 4h Soll, kein Credit.
// ----------------------------------------------------------------------------
export function buildEmployeeSummary({
  employees,
  entries,
  holidays,
  vacations = [],
  absences = [],
  dateRange,
  todayStatus,
}) {
  const holidayDateSet = new Set(
    (holidays || [])
      .map((h) => h.date)
      .filter((d) => d >= dateRange.start && d <= dateRange.end)
  );

  return employees.map((emp) => {
    const empEntries = entries.filter((e) => e.employee_id === emp.id);
    const workEntries = empEntries.filter((e) => e.entry_type !== 'vacation');

    const totalWork = empEntries.reduce((sum, e) => sum + (e.total_work_minutes || 0), 0);
    const totalBreak = workEntries.reduce((sum, e) => sum + (e.total_break_minutes || 0), 0);
    const totalNet = empEntries.reduce((sum, e) => sum + (e.net_work_minutes || 0), 0);
    const workDays = workEntries.filter((e) => e.clock_out).length;

    // Aus den vollen Listen Tage in Range filtern
    const inRange = (d) => d >= dateRange.start && d <= dateRange.end;

    // Urlaubs-Tage (ganz/halb) zaehlen — nur Mo-Fr, nicht an Feiertagen
    let vacationDaysFull = 0;
    let vacationDaysHalf = 0;
    (vacations || [])
      .filter((v) => v.employee_id === emp.id && v.status === 'approved')
      .forEach((v) => {
        const half = isVacationHalfDay(v);
        eachDate(v.start_date, v.end_date).forEach((d) => {
          if (!inRange(d)) return;
          const dow = new Date(d).getDay();
          if (dow === 0 || dow === 6) return;
          if (holidayDateSet.has(d)) return;
          if (half) vacationDaysHalf += 1;
          else vacationDaysFull += 1;
        });
      });
    const vacationDays = vacationDaysFull + vacationDaysHalf / 2; // ggf. 0.5 / 1.5 / ...

    // Abwesenheits-Tage (Krank/Kind-krank/EZ etc.)
    let sickDays = 0;
    let absenceOtherDays = 0;
    (absences || [])
      .filter((a) => a.employee_id === emp.id)
      .forEach((a) => {
        eachDate(a.start_date, a.end_date).forEach((d) => {
          if (!inRange(d)) return;
          const dow = new Date(d).getDay();
          if (dow === 0 || dow === 6) return;
          if (holidayDateSet.has(d)) return;
          const t = a.absence_type;
          if (t === 'K' || t === 'KO' || t === 'KK') sickDays += 1;
          else absenceOtherDays += 1;
        });
      });

    let workDaysExpected = 0;
    let holidaysInPeriod = 0;
    eachDate(dateRange.start, dateRange.end).forEach((dateStr) => {
      const dow = new Date(dateStr).getDay();
      if (dow >= 1 && dow <= 5) {
        if (holidayDateSet.has(dateStr)) holidaysInPeriod += 1;
        else workDaysExpected += 1;
      }
    });

    const dailyMinutes = ((emp.weekly_hours || 40) * 60) / 5;

    // Soll: nur tatsaechliche Arbeitstage (ohne Feiertag/Urlaub/Krank).
    // Halbtags-Urlaub: 0.5 Tag Soll bleibt erhalten.
    const reducedDaysFromAbsence = vacationDaysFull + vacationDaysHalf * 0.5 + sickDays + absenceOtherDays;
    const effectiveWorkDays = Math.max(0, workDaysExpected - reducedDaysFromAbsence);
    const expectedMinutes = effectiveWorkDays * dailyMinutes;

    const totalWithHolidays = totalNet; // kein automatischer Credit mehr — Soll ist bereits reduziert
    const todayEntry = todayStatus.find((e) => e.employee_id === emp.id);

    // Tages-Status-Map fuer Detail-Drawer
    const dayStatusMap = buildDayStatusMap({
      employeeId: emp.id,
      dateRange,
      workEntries,
      vacations,
      absences,
      holidayDateSet,
      dailyMinutesTarget: dailyMinutes,
    });

    return {
      ...emp,
      totalWork,
      totalBreak,
      totalNet,
      totalWithHolidays,
      workDays,
      vacationDays,           // float bei halben Tagen
      vacationDaysFull,
      vacationDaysHalf,
      sickDays,
      absenceOtherDays,
      holidaysInPeriod,
      expectedMinutes,
      difference: totalWithHolidays - expectedMinutes,
      todayStatus: todayEntry?.status || 'not_clocked',
      todayEntry,
      dayStatusMap,
    };
  });
}

export function computeStatusCounts(todayStatus, employees) {
  const todayByEmployee = {};
  todayStatus.forEach((e) => {
    if (!todayByEmployee[e.employee_id] || e.clock_in > todayByEmployee[e.employee_id].clock_in) {
      todayByEmployee[e.employee_id] = e;
    }
  });
  const uniqueTodayStatus = Object.values(todayByEmployee);
  return {
    working: uniqueTodayStatus.filter((e) => e.status === 'clocked_in').length,
    onBreak: uniqueTodayStatus.filter((e) => e.status === 'on_break').length,
    finished: uniqueTodayStatus.filter((e) => e.status === 'clocked_out').length,
    notClocked: Math.max(0, employees.length - uniqueTodayStatus.length),
  };
}

// Heutige Abwesenheits-Aggregate (fuer KPI-Kachel) — zaehlt aus den
// dayStatusMaps aller Mitarbeiter:innen genau den heutigen Tag.
export function computeAbsenceCountsToday(employeeSummary) {
  const today = new Date().toISOString().split('T')[0];
  const counts = { vacation: 0, sick: 0, absenceOther: 0, holiday: 0 };
  employeeSummary.forEach((emp) => {
    const s = emp.dayStatusMap?.[today];
    if (!s) return;
    if (s.type === DAY_TYPES.VACATION || s.type === DAY_TYPES.VACATION_HALF) counts.vacation += 1;
    else if (s.type === DAY_TYPES.SICK) counts.sick += 1;
    else if (s.type === DAY_TYPES.ABSENCE_OTHER) counts.absenceOther += 1;
    else if (s.type === DAY_TYPES.HOLIDAY) counts.holiday += 1;
  });
  return counts;
}
